import {
	ApplicationRef,
	Component,
	createComponent,
	ElementRef,
	EnvironmentInjector,
	inject,
	viewChild,
	ComponentRef,
	DestroyRef,
	output,
	effect,
	model,
	inputBinding,
	twoWayBinding,
} from '@angular/core'
import {
	NgxExtendedPdfViewerModule,
	PageRenderedEvent,
} from 'ngx-extended-pdf-viewer'
import { input } from '@angular/core'
import {
	RedactionBoxInternal,
	RedactionLayerComponent,
} from '../redaction-layer/redaction-layer.component'

let instanceCounter = 0

@Component({
	selector: 'lowgile-pdf',
	templateUrl: './pdf.component.html',
	styleUrls: ['./pdf.component.scss'],
	standalone: true,
	imports: [NgxExtendedPdfViewerModule],
})
export class PdfComponent {
	protected instanceId = `pdf-viewer-${++instanceCounter}`
	private appRef = inject(ApplicationRef)
	private envInjector = inject(EnvironmentInjector)
	private destroyRef = inject(DestroyRef)
	private pdfViewerEl = viewChild('pdfViewer', {
		read: ElementRef,
	})

	url = input<string>()
	base64Src = input<string>()
	height = input<string>()
	redactionMode = input(false)
	redactions = model<RedactionBoxInternal[][]>()

	// redactionsChanged = output<RedactionBoxInternal[][]>()

	private pageData: PageData[] = []

	constructor() {
		effect(() => {
			const redactionMode = this.redactionMode()
			for (const pageData of this.pageData) {
				pageData.redactionLayerRef?.setInput('redactionMode', redactionMode)
			}
		})
		// Stop propagation of mousemove event already at capture phase to prevent the native PDF viewer from scrolling.
		// The redaction layer will listen to pointerdown events instead.
		const onMouseDown = (event: MouseEvent) => {
			if (this.redactionMode()) {
				event.stopPropagation()
			}
		}
		window.addEventListener('mousedown', onMouseDown, true)
		this.destroyRef.onDestroy(() => {
			window.removeEventListener('mousedown', onMouseDown)
		})
	}

	private getOrCreatePageData(canvas: HTMLCanvasElement): PageData {
		const canvasIdx = this.getAllCanvasEls().indexOf(canvas)
		const pageDiv = canvas.parentElement.parentElement
		let overlayDiv = Array.from(pageDiv.children).find((child) =>
			child.classList.contains('redaction-overlay'),
		) as HTMLDivElement | undefined
		const pageData: PageData = this.pageData[canvasIdx] || {
			overlayDiv: undefined,
			canvas: undefined,
			redactionLayerRef: undefined,
			redactionBoxes: this.redactions()?.[canvasIdx] ?? [],
		}
		this.pageData[canvasIdx] = pageData

		if (!overlayDiv) {
			if (pageData.redactionLayerRef) {
				this.appRef.detachView(pageData.redactionLayerRef.hostView)
				pageData.redactionLayerRef = undefined
			}
			pageData.overlayDiv = overlayDiv = document.createElement('div')
			overlayDiv.classList.add('redaction-overlay')
			pageDiv.appendChild(overlayDiv)

			const redactionLayerRef = createComponent(RedactionLayerComponent, {
				hostElement: overlayDiv,
				environmentInjector: this.envInjector,
			})
			redactionLayerRef.setInput('redactionBoxes', pageData.redactionBoxes)
			redactionLayerRef.setInput('redactionMode', this.redactionMode())
			redactionLayerRef.instance.redactionsChanged.subscribe(() =>
				this.onRedactionsChanged(),
			)

			this.appRef.attachView(redactionLayerRef.hostView)
			pageData.redactionLayerRef = redactionLayerRef
			pageData.canvas = canvas
			redactionLayerRef.changeDetectorRef.detectChanges()
		}
		return pageData
	}

	private getAllCanvasEls() {
		return Array.from(
			this.pdfViewerEl()?.nativeElement.querySelectorAll('canvas') || [],
		) as HTMLCanvasElement[]
	}

	onPageRendered(event: PageRenderedEvent) {
		this.getOrCreatePageData(event.source.canvas)
	}

	onRedactionsChanged() {
		this.redactions.set(
			this.pageData.map((pageData) => pageData.redactionBoxes),
		)
	}
}

interface PageData {
	overlayDiv: HTMLDivElement
	canvas: HTMLCanvasElement
	redactionBoxes: RedactionBoxInternal[]
	redactionLayerRef: ComponentRef<RedactionLayerComponent>
}
